// Asking the browser for a new service worker, and telling the app once one is
// ready.
//
// The mechanism, because it is not where you would look for it: the browser
// only checks whether `service-worker.js` has changed while it is handling a
// navigation request in scope. This app is a react-router SPA, so after the
// first load it makes no navigations at all -- every route change is
// client-side. Installed to a home screen it is suspended and resumed rather
// than closed, so the `load` event that serviceWorker.js registers on never
// fires a second time either. Nothing asks, so nothing updates, and a session
// that never ends never sees a deploy.
//
// Observed in production in September 2026: three accounts in one household
// were still running a build that predated the reveal-answer and lesson-view
// endpoints, months after those shipped. Their got-it clicks recorded normally,
// because that endpoint is older than their build, so nothing looked broken --
// the newer tracking simply was not being sent, and an audit of those students
// showed "no reveal clicks" when the truth was "no reveal data". Unmeasured
// read as clean, which is the failure this product can least afford.
//
// Making discovery reliable is only half the job, and the dangerous half on its
// own: sw-custom.js used to call skipWaiting(), so a discovered worker took over
// pages that were still running the previous build's JavaScript and evicted the
// chunks they expected. That unconditional call is gone, and a new worker now
// waits until UpdateAvailableBanner's Reload button releases it. Read the comment
// at the top of sw-custom.js before putting it back.
//
// None of which helps the clients in the paragraph above, and that is worth being
// blunt about: this file is not in their bundle. Nothing in an old build asks for
// an update or promotes one, so making discovery reliable here reaches only the
// builds that already had it. The rescue for the rest had to move into the new
// WORKER, which asks the open windows whether they can offer the update
// themselves and reloads the ones that cannot answer. See
// utils/serviceWorkerCapability.js and the forced-update section of
// sw-custom.js. What this file provides there is the thing a CURRENT build needs
// in order to answer "leave me alone" and be believed.

// Long enough that a tab left open all evening is not making pointless
// requests; short enough that a deploy reaches a long-lived session same-day.
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000

// visibilitychange fires on every app switch, and a child moving between apps
// produces bursts of them. This floor is what makes the visibility trigger safe
// to leave as eager as it is. It bounds how often a check *can* happen, not how
// often one does: a trigger refused by the floor is dropped, not queued.
export const MIN_CHECK_GAP_MS = 60 * 1000

const attempt = (registration, state, win) => {
	// Not merely defensive: returning early here leaves state.lastCheck alone,
	// so a browser without registration.update never burns its gap budget on a
	// check that cannot happen. The try/catch below would swallow the TypeError
	// but would already have spent it.
	if (!registration || typeof registration.update !== "function") return false

	// A check made offline cannot succeed, and reloading is not the answer to a
	// lost connection -- OfflineIndicator already owns that story.
	if (win.navigator && win.navigator.onLine === false) return false

	const now = Date.now()

	if (state.lastCheck && now - state.lastCheck < MIN_CHECK_GAP_MS) return false

	state.lastCheck = now

	try {
		// update() rejects when the network fails or the worker script 404s.
		// Neither is actionable by a child on an iPad, and an unhandled
		// rejection would land in the same window listener staleBuild.js
		// watches -- a channel worth keeping quiet, even though a rejection
		// from here would not match its chunk-error test.
		Promise.resolve(registration.update()).catch(() => {})
	} catch (e) {
		// A few browsers throw synchronously from update() when the worker is
		// already mid-replacement. That is the state we wanted anyway.
	}

	return true
}

/**
 * Start asking for updates. Returns a teardown function.
 *
 * @param {ServiceWorkerRegistration} registration
 * @param {Window} win
 */
export const installUpdateChecks = (registration, win = window) => {
	if (!registration) return () => {}

	const state = { lastCheck: 0 }
	const check = () => attempt(registration, state, win)

	// The one that matters most. Resuming a suspended home-screen app is the
	// only moment such an app reliably runs code again without a page load, so
	// it is the only reliable hook into the exact clients this exists for.
	const onVisibility = () => {
		if (win.document.visibilityState === "visible") check()
	}

	// A device that was offline when the interval fired gets its check when the
	// network returns, rather than waiting out another full interval.
	const onOnline = () => check()

	win.document.addEventListener("visibilitychange", onVisibility)
	win.addEventListener("online", onOnline)

	const timer = win.setInterval(check, UPDATE_CHECK_INTERVAL_MS)

	return () => {
		win.document.removeEventListener("visibilitychange", onVisibility)
		win.removeEventListener("online", onOnline)
		win.clearInterval(timer)
	}
}

/**
 * Whether a new worker has *already* finished installing and is waiting to take
 * over, which is a state nothing else will ever tell us about.
 *
 * `updatefound` fires once, when the browser starts installing. The browser
 * begins its update check as part of the navigation, while serviceWorker.js
 * attaches that handler later, on `load` -- so a worker that installs quickly
 * can be waiting before anything is listening. No later check rescues it
 * either: update() on a worker that is already installed finds nothing to do
 * and fires no event. Without this the worker sits in `waiting` indefinitely,
 * the user is never told, and the only way out is closing every tab.
 *
 * This is not hypothetical -- it is what happened on the deploy that first
 * shipped the banner. The page was still running the previous build, which had
 * no banner in it, so the new worker installed, waited, and went unannounced.
 *
 * The controller check is what separates an update from a first install: with no
 * controller there is no older build to replace, so there is nothing to say.
 *
 * @param {ServiceWorkerRegistration} registration
 * @param {Window} win
 */
export const hasWaitingWorker = (registration, win = window) => {
	if (!registration || !registration.waiting) return false

	const container = win.navigator && win.navigator.serviceWorker

	return Boolean(container && container.controller)
}

// serviceWorker.register() is called from index.js, outside React, and its
// onUpdate callback can fire before the tree has mounted. So the announcement
// latches: a subscriber arriving late is told immediately rather than missing
// the only announcement it will get for this deploy.
//
// Each announcement carries a sequence number because the *registration* cannot
// tell one from another -- the browser hands back the same
// ServiceWorkerRegistration object for the scope every time, so a second deploy
// announces an object identical by reference to the first. Anything that has to
// notice "this is new news" (UpdateAvailableBanner resetting a dismissal) needs
// the sequence, not the registration.
let announced = null
let sequence = 0
const listeners = new Set()

/**
 * Record that a new worker is installed and ready to take over. Passed to
 * serviceWorker.register() as its `onUpdate` callback.
 *
 * @param {ServiceWorkerRegistration} registration
 */
export const announceUpdate = registration => {
	sequence += 1
	announced = { registration: registration || {}, sequence }

	listeners.forEach(listener => {
		try {
			listener(announced)
		} catch (e) {
			// One subscriber throwing must not cost the others their
			// announcement, nor propagate into the browser's onstatechange
			// handler that called us.
		}
	})
}

/**
 * @param {function} listener called with `{ registration, sequence }`
 * @returns {function} unsubscribe
 */
export const subscribeToUpdates = listener => {
	listeners.add(listener)

	if (announced) listener(announced)

	return () => {
		listeners.delete(listener)
	}
}

/** Test seam: module state outlives a single test otherwise. */
export const resetUpdateAnnouncements = () => {
	announced = null
	sequence = 0
	listeners.clear()
}
